/**
 * Plan proxy rendering + default placement — shared by the Plans ticket and
 * the Vector Layout import (WP-A one-upload convergence: importing a layout
 * PDF also creates the companion plan overlay, so the same rendering rules
 * must live in one place).
 */

import { useMapStore } from '../stores/mapStore';

/**
 * Browser render of a PDF page — the plan's PROXY image, not the final
 * artifact (full resolution comes from the server-side bake).
 *
 * Sizing is deliberately MODEST (4096px cap, lowered further on weak GPUs):
 * trusting MAX_TEXTURE_SIZE once authorized a 114MP render that the map could
 * not turn into a texture, so the plan silently rendered NOTHING. See the
 * plan-bake phase history before raising this.
 */
export async function pdfToTransparentPng(
  file: File,
  pageNumber = 1,
  bgThreshold = 245,
): Promise<{ url: string; pageWidthPt: number; pageHeightPt: number }> {
  const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(pageNumber);
  const baseViewport = page.getViewport({ scale: 1 });
  const PROXY_MAX_DIM = 4096;
  let maxDim = PROXY_MAX_DIM;
  try {
    const mapCanvas = useMapStore.getState().map?.getCanvas();
    const gl = mapCanvas?.getContext('webgl') || mapCanvas?.getContext('webgl2');
    const glMax = gl ? (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) : 0;
    // Only ever lower the cap — a weak GPU must still be respected.
    if (glMax > 0) maxDim = Math.min(PROXY_MAX_DIM, glMax);
  } catch (_e) { /* keep the conservative default */ }
  const scale = Math.min(maxDim / baseViewport.width, maxDim / baseViewport.height, 3);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport, canvas } as never).promise;
  if (bgThreshold > 0) {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const px = imageData.data;
    for (let i = 0; i < px.length; i += 4) {
      if (px[i] >= bgThreshold && px[i + 1] >= bgThreshold && px[i + 2] >= bgThreshold) px[i + 3] = 0;
    }
    ctx.putImageData(imageData, 0, 0);
  }
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  // Release canvas GPU memory immediately
  canvas.width = 0;
  canvas.height = 0;
  if (!blob) throw new Error('Failed to create PNG from PDF');
  return {
    url: URL.createObjectURL(blob),
    pageWidthPt: baseViewport.width,
    pageHeightPt: baseViewport.height,
  };
}

/** Default on-map placement for a new plan: ~250×150m centered on the view.
 *  A registered layout replaces this via pageImageCorners the moment anchors
 *  exist — this is only where the sheet waits meanwhile. */
export function centerPlacementBounds(): [[number, number], [number, number], [number, number], [number, number]] {
  const map = useMapStore.getState().map;
  const center = map
    ? map.getCenter()
    : { lng: useMapStore.getState().center[0], lat: useMapStore.getState().center[1] };
  const wMeters = 250, hMeters = 150, mpdLat = 111320;
  const mpdLng = 111320 * Math.cos(center.lat * Math.PI / 180);
  const dLng = wMeters / mpdLng / 2;
  const dLat = hMeters / mpdLat / 2;
  return [
    [center.lng - dLng, center.lat + dLat],
    [center.lng + dLng, center.lat + dLat],
    [center.lng + dLng, center.lat - dLat],
    [center.lng - dLng, center.lat - dLat],
  ];
}
